/**
 * «Последняя область» — что человек выбирал в переключателе «Хозяйство ▾»
 * в прошлый раз (AK-896; AK-866 В-3: «вход → карточка ПОСЛЕДНЕГО хозяйства»).
 *
 * Зачем, если область и так лежит в сессии (`/scope/set` →
 * `auth.sessions.active_scope_*`): сессия — на вход. Новый логин (и
 * `select-tenant`, #800) обнуляет указатель, и `/scope/available` отдаёт
 * `current = available[0]` — холдинг/компанию, дефолт сервера, а не выбор
 * человека. Чтобы после входа открыть то хозяйство, с которым он работал,
 * нужна память ВНЕ сессии.
 *
 * Где лежит: `localStorage` этого браузера — удобство для зрителя, не данные.
 * Серверной ручки под это нет: `PATCH /users/me/map-preferences` принимает
 * только поля карты (`MapPreferencesPatchV1`, `extra="forbid"` → 422 на любое
 * новое поле), а `/scope/set` — это и есть сессия. Нужная ручка бэкенда —
 * поле `last_scope` в prefs членства (`auth.tenant_members`, рядом с
 * `map_prefs`: естественно скоупится user × tenant и переживает смену
 * браузера) — отдельная задача head-backend; когда она появится, этот модуль
 * станет её клиентом, а чтение/запись здесь останутся единственной точкой.
 *
 * Что запоминаем — ВЫБОР, а не только хозяйство: «Холдинг» тоже выбор.
 * Иначе руководитель, живущий в «Холдинге», после каждого входа улетал бы в
 * хозяйство, которое открывал полгода назад, — усталость от системы (CLAUDE.md
 * «отсутствие усталости»). Кластер/отделение не запоминаем: в переключателе
 * они не выбираются.
 *
 * Чтение всегда сверяется с `/scope/available` на входе (`resolveEntryTarget`):
 * хозяйство из памяти, которого больше нет в списке (сменили кабинет, отобрали
 * доступ, другой пользователь на этом браузере), просто не учитывается. Поэтому
 * память не привязана к user/tenant явно — список доступного и есть граница.
 *
 * `try/catch` на каждом шаге: Safari private mode бросает на `setItem`,
 * расширения подменяют `localStorage`, старые записи могут быть не JSON.
 * Память — удобство, ронять из-за неё вход нельзя.
 */
import type { ScopeSelection } from './ScopeTree';

export const LAST_SCOPE_KEY = 'ikolhoz:last-scope:v1';

export type LastScope = { kind: 'farm'; farmId: string } | { kind: 'holding' };

interface StoredLastScope {
  kind: 'farm' | 'holding';
  farm_id?: string | null;
  /** ISO-время записи — для отладки и будущей миграции на серверные prefs. */
  at?: string;
}

export function readLastScope(): LastScope | null {
  try {
    const raw = localStorage.getItem(LAST_SCOPE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredLastScope> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.kind === 'holding') return { kind: 'holding' };
    if (parsed.kind === 'farm' && typeof parsed.farm_id === 'string' && parsed.farm_id) {
      return { kind: 'farm', farmId: parsed.farm_id };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Запомнить выбор. Зовётся из двух мест, где выбор делает ЧЕЛОВЕК: клик в
 * переключателе (`FarmSwitcher.pick`) и отработавшая ссылка `?farm=`
 * (`useFarmDeepLink`). Авто-подстановка на входе (первое по алфавиту) сюда не
 * пишет: это догадка системы, а не выбор.
 */
export function rememberLastScope(s: ScopeSelection): void {
  let next: StoredLastScope | null = null;
  if (s.scope_type === 'farm' && s.scope_id) next = { kind: 'farm', farm_id: s.scope_id };
  else if (s.scope_type === 'company' || s.scope_type === 'holding' || s.scope_type === 'tenant') {
    next = { kind: 'holding' };
  }
  if (!next) return;
  try {
    localStorage.setItem(LAST_SCOPE_KEY, JSON.stringify({ ...next, at: new Date().toISOString() }));
  } catch {
    // Память — удобство: некуда писать — значит, не запомнили.
  }
}

/** Только для тестов — очистить память между кейсами. */
export function __clearLastScope(): void {
  try {
    localStorage.removeItem(LAST_SCOPE_KEY);
  } catch {
    // окружение без storage — чистить нечего
  }
}
